<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h1 class="page-title">預約總覽</h1>
      </div>
      <router-link :to="{ name: 'SalonReservationCreate' }" class="btn btn-primary">
        <i class="ri-add-line"></i>
        新增預約
      </router-link>
    </div>

    <section class="store-filter">
      <div class="section-heading">
        <div>
          <h2>選擇門市</h2>
          <p>一次顯示一間門市，可切換行事曆或預約列表</p>
        </div>
        <span v-if="selectedStore" class="reservation-count">
          {{ reservationCountLabel }}
        </span>
      </div>
      <StoreSelector
        v-model="selectedStoreId"
        :stores="stores"
        :loading="storesLoading"
        :clearable="false"
        @change="handleStoreChange"
      />
    </section>

    <section class="calendar-card">
      <div v-if="!storesLoading && stores.length === 0" class="empty-state">
        <i class="ri-store-2-line"></i>
        <strong>目前沒有開放美髮預約的門市</strong>
        <span>請先到預約設定開啟門市預約，再查看總覽。</span>
      </div>
      <div v-else-if="selectedStoreId" class="calendar-wrapper">
        <div class="display-mode-toolbar">
          <div class="display-mode-switch" role="group" aria-label="預約顯示模式">
            <button
              type="button"
              :class="{ active: displayMode === 'calendar' }"
              @click="setDisplayMode('calendar')"
            >
              <i class="ri-calendar-line"></i>行事曆
            </button>
            <button
              type="button"
              :class="{ active: displayMode === 'list' }"
              @click="setDisplayMode('list')"
            >
              <i class="ri-list-check-2"></i>預約列表
            </button>
          </div>
          <label v-if="displayMode === 'list'" class="upcoming-toggle">
            <input v-model="upcomingOnly" type="checkbox" @change="loadReservationList" />
            只顯示尚未過期
          </label>
        </div>
        <div v-if="displayMode === 'calendar' && showMissingHoursWarning" class="hours-warning">
          <i class="ri-error-warning-line"></i>
          <div>
            <strong>此門市尚未設定可營業時段</strong>
            <span>目前視為全日不營業，無法提供預約時段。</span>
          </div>
          <router-link
            :to="{ name: 'SalonSettingsEdit', params: { storeId: selectedStoreId } }"
            class="btn btn-sm btn-secondary"
          >
            前往設定
          </router-link>
        </div>
        <div v-show="displayMode === 'calendar'">
          <div class="calendar-legend">
            <span><i class="legend-swatch business"></i>營業時段</span>
            <span><i class="legend-swatch non-business"></i>非營業時段</span>
            <span><i class="legend-swatch closure"></i>整日店休</span>
          </div>
          <div v-if="calendarLoading" class="calendar-loading">
            <i class="ri-loader-4-line spin"></i>
            載入門市行事曆中...
          </div>
          <FullCalendar ref="calendar" :options="calendarOptions" />
        </div>
        <div v-if="displayMode === 'list'" class="reservation-list-view">
          <div v-if="listLoading" class="list-loading">
            <i class="ri-loader-4-line spin"></i>載入預約列表中...
          </div>
          <div v-else-if="!reservationList.length" class="empty-state compact">
            <i class="ri-calendar-check-line"></i>
            <strong>{{ upcomingOnly ? '目前沒有尚未過期的預約' : '目前沒有預約資料' }}</strong>
          </div>
          <div v-else class="table-wrapper">
            <table class="reservation-table">
              <thead>
                <tr>
                  <th>預約時間</th>
                  <th>顧客</th>
                  <th>手機</th>
                  <th>設計師</th>
                  <th>狀態</th>
                  <th>預約編號</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in reservationList" :key="item.id">
                  <td>
                    <strong>{{ displayReservationDate(item) }}</strong>
                  </td>
                  <td>{{ item.contactName || '-' }}</td>
                  <td>{{ item.contactPhone || '-' }}</td>
                  <td>{{ item.stylistName || '-' }}</td>
                  <td>
                    <span
                      class="status-badge"
                      :style="{ '--status-color': statusStyle(item.status).color }"
                    >
                      {{ statusStyle(item.status).label }}
                    </span>
                  </td>
                  <td>{{ item.reservationNumber }}</td>
                  <td>
                    <button type="button" class="btn btn-sm btn-secondary" @click="openReservationItem(item)">
                      查看
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import zhTwLocale from '@fullcalendar/core/locales/zh-tw';
import StoreSelector from '@/components/StoreSelector.vue';
import { salonService } from '@/api/modules/salon.js';
import { storeService } from '@/api/modules/stores.js';
import { salonDateOnly } from '@/utils/salonDate.js';
import { showError } from '@/utils/swal.js';

const statusStyles = {
  confirmed: { label: '已確認', color: '#2563eb' },
  checked_in: { label: '已報到', color: '#7c3aed' },
  in_service: { label: '服務中', color: '#d97706' },
  completed: { label: '已完成', color: '#059669' },
  cancelled: { label: '已取消', color: '#94a3b8' },
  no_show: { label: '未到店', color: '#dc2626' }
};

function localDateKey(value) {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDateDays(value, days) {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function closureAppliesOnDate(rule, dateValue) {
  const startDate = salonDateOnly(rule.startDate);
  const endDate = salonDateOnly(rule.endDate);
  if (dateValue < startDate || (endDate && dateValue > endDate)) return false;
  if (rule.recurrenceType === 'none') return true;

  const date = new Date(`${dateValue}T00:00:00Z`);
  const start = new Date(`${startDate}T00:00:00Z`);
  const interval = Number(rule.recurrenceInterval) || 1;
  if (rule.recurrenceType === 'weekly') {
    const weeks = Math.floor((date - start) / (7 * 24 * 60 * 60 * 1000));
    return (rule.daysOfWeek || []).map(Number).includes(date.getUTCDay())
      && weeks % interval === 0;
  }
  if (rule.recurrenceType === 'monthly') {
    const months = (date.getUTCFullYear() - start.getUTCFullYear()) * 12
      + date.getUTCMonth() - start.getUTCMonth();
    return (rule.daysOfMonth || []).map(Number).includes(date.getUTCDate())
      && months % interval === 0;
  }
  return false;
}

export default {
  name: 'SalonOverview',
  components: { FullCalendar, StoreSelector },
  data() {
    return {
      stores: [],
      storesLoading: false,
      hoursLoading: false,
      reservationsLoading: false,
      hasConfiguredHours: null,
      selectedStoreId: '',
      visibleRange: null,
      events: [],
      requestSequence: 0,
      displayMode: 'calendar',
      upcomingOnly: true,
      reservationList: [],
      listLoading: false,
      listRequestSequence: 0,
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        locale: zhTwLocale,
        initialView: 'timeGridWeek',
        firstDay: 1,
        height: 'auto',
        nowIndicator: true,
        allDaySlot: false,
        slotMinTime: '08:00:00',
        slotMaxTime: '22:00:00',
        slotDuration: '00:30:00',
        scrollTime: '09:00:00',
        dayMaxEvents: true,
        eventDisplay: 'block',
        businessHours: [],
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'timeGridDay,timeGridWeek,dayGridMonth'
        },
        buttonText: {
          today: '今天',
          day: '當日',
          week: '整週',
          month: '整月'
        },
        events: (info, successCallback, failureCallback) =>
          this.fetchCalendarEvents(info, successCallback, failureCallback),
        datesSet: info => this.handleDatesSet(info),
        eventClick: info => this.openReservation(info),
        eventDidMount: info => {
          const closure = info.event.extendedProps.closure;
          if (closure) {
            info.el.title = `整日店休｜${closure.name}`;
            return;
          }
          const item = info.event.extendedProps.reservation;
          const status = statusStyles[item.status]?.label || item.status;
          info.el.title = [
            `${this.shortTime(item.startTime)}–${this.shortTime(item.endTime)}`,
            item.contactName || '未填姓名',
            item.stylistName,
            status
          ].join('｜');
        }
      }
    };
  },
  computed: {
    selectedStore() {
      return this.stores.find(store => store.id === this.selectedStoreId) || null;
    },
    visibleRangeLabel() {
      if (!this.visibleRange) return '';
      const labels = {
        timeGridDay: '當日',
        timeGridWeek: '本週',
        dayGridMonth: '本月'
      };
      return labels[this.visibleRange.viewType] || '目前區間';
    },
    reservationCountLabel() {
      if (this.displayMode === 'list') {
        return `${this.upcomingOnly ? '尚未過期' : '全部'}共 ${this.reservationList.length} 筆`;
      }
      return `${this.visibleRangeLabel}共 ${this.events.length} 筆`;
    },
    calendarLoading() {
      return this.hoursLoading || this.reservationsLoading;
    },
    showMissingHoursWarning() {
      return this.hasConfiguredHours === false;
    }
  },
  async mounted() {
    await this.loadStores();
  },
  methods: {
    async loadStores() {
      this.storesLoading = true;
      try {
        const [response, settings] = await Promise.all([
          storeService.getStores({
            page: 1,
            pageSize: 100,
            status: 'active',
            sortBy: 'storeName',
            order: 'asc'
          }),
          salonService.listSettings()
        ]);
        const openStoreIds = new Set(
          (settings?.data || [])
            .filter(item => item.bookingEnabled)
            .map(item => item.storeId)
        );
        this.stores = (response?.data?.items || [])
          .filter(store => openStoreIds.has(store.id));
        if (!this.selectedStoreId && this.stores.length) {
          this.selectedStoreId = this.stores[0].id;
          this.$nextTick(() => this.handleStoreChange());
        }
      } catch (error) {
        await this.handleError(error, '門市載入失敗');
      } finally {
        this.storesLoading = false;
      }
    },
    handleDatesSet(info) {
      const inclusiveEnd = new Date(info.end);
      inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
      this.visibleRange = {
        startDate: localDateKey(info.start),
        endDate: localDateKey(inclusiveEnd),
        viewType: info.view.type
      };
    },
    refreshCalendar() {
      this.$refs.calendar?.getApi().refetchEvents();
    },
    async handleStoreChange() {
      await this.loadStoreHours();
      if (this.displayMode === 'list') {
        await this.loadReservationList();
      } else {
        this.refreshCalendar();
      }
    },
    async setDisplayMode(mode) {
      if (this.displayMode === mode) return;
      this.displayMode = mode;
      if (mode === 'list') {
        await this.loadReservationList();
      } else {
        await this.$nextTick();
        this.$refs.calendar?.getApi().updateSize();
      }
    },
    async loadReservationList() {
      if (!this.selectedStoreId) {
        this.reservationList = [];
        return;
      }
      const sequence = ++this.listRequestSequence;
      const storeId = this.selectedStoreId;
      this.listLoading = true;
      try {
        const params = {
          page: 1,
          pageSize: 100,
          storeId,
          upcomingOnly: this.upcomingOnly,
          sortOrder: 'asc'
        };
        const firstResponse = await salonService.listReservations(params);
        const firstData = firstResponse?.data || {};
        const items = [...(firstData.items || [])];
        const totalPages = firstData.pagination?.totalPages || 1;
        if (totalPages > 1) {
          const remaining = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) =>
              salonService.listReservations({ ...params, page: index + 2 })
            )
          );
          remaining.forEach(response => items.push(...(response?.data?.items || [])));
        }
        if (sequence !== this.listRequestSequence || storeId !== this.selectedStoreId) return;
        this.reservationList = items.sort((left, right) => (
          this.reservationTimestamp(left) - this.reservationTimestamp(right)
        ));
      } catch (error) {
        if (sequence === this.listRequestSequence) {
          this.reservationList = [];
          await this.handleError(error, '預約列表載入失敗');
        }
      } finally {
        if (sequence === this.listRequestSequence) this.listLoading = false;
      }
    },
    async loadStoreHours() {
      if (!this.selectedStoreId) {
        this.hasConfiguredHours = null;
        this.calendarOptions.businessHours = [];
        return;
      }
      const storeId = this.selectedStoreId;
      this.hasConfiguredHours = null;
      this.hoursLoading = true;
      try {
        const response = await storeService.getStoreHours(storeId);
        if (storeId !== this.selectedStoreId) return;
        const hours = response?.data?.hours || [];
        const businessHours = hours.flatMap(day => {
          if (!day.isOpen) return [];
          let slots = Array.isArray(day.timeSlots)
            ? day.timeSlots.filter(slot => slot?.openTime && slot?.closeTime)
            : [];
          if (!slots.length && day.openTime && day.closeTime) {
            slots = day.breakStart && day.breakEnd
              ? [
                { openTime: day.openTime, closeTime: day.breakStart },
                { openTime: day.breakEnd, closeTime: day.closeTime }
              ]
              : [{ openTime: day.openTime, closeTime: day.closeTime }];
          }
          return slots.map(slot => ({
            daysOfWeek: [Number(day.dayOfWeek)],
            startTime: this.shortTime(slot.openTime),
            endTime: this.shortTime(slot.closeTime)
          }));
        });
        this.hasConfiguredHours = businessHours.length > 0;
        this.calendarOptions.businessHours = businessHours.length
          ? businessHours
          : [{
            daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
            startTime: '00:00:00',
            endTime: '00:00:01'
          }];
      } catch (error) {
        if (storeId === this.selectedStoreId) {
          this.hasConfiguredHours = null;
          this.calendarOptions.businessHours = [];
          await this.handleError(error, '門市營業時間載入失敗');
        }
      } finally {
        if (storeId === this.selectedStoreId) this.hoursLoading = false;
      }
    },
    async fetchCalendarEvents(info, successCallback, failureCallback) {
      if (!this.selectedStoreId) {
        this.events = [];
        successCallback([]);
        return;
      }

      const sequence = ++this.requestSequence;
      this.reservationsLoading = true;
      try {
        const inclusiveEnd = new Date(info.end);
        inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
        const params = {
          page: 1,
          pageSize: 100,
          storeId: this.selectedStoreId,
          startDate: localDateKey(info.start),
          endDate: localDateKey(inclusiveEnd)
        };
        const [firstResponse, closures] = await Promise.all([
          salonService.listReservations(params),
          this.loadCalendarClosures()
        ]);
        const firstData = firstResponse?.data || {};
        const items = [...(firstData.items || [])];
        const totalPages = firstData.pagination?.totalPages || 1;

        if (totalPages > 1) {
          const remaining = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) =>
              salonService.listReservations({ ...params, page: index + 2 })
            )
          );
          remaining.forEach(response => {
            items.push(...(response?.data?.items || []));
          });
        }

        if (sequence !== this.requestSequence) {
          successCallback([]);
          return;
        }
        this.events = items.map(this.toCalendarEvent);
        const closureEvents = this.toClosureBackgroundEvents(closures, info.start, info.end);
        successCallback([...this.events, ...closureEvents]);
      } catch (error) {
        if (sequence === this.requestSequence) {
          this.events = [];
          failureCallback(error);
          await this.handleError(error, '預約載入失敗');
        }
      } finally {
        if (sequence === this.requestSequence) this.reservationsLoading = false;
      }
    },
    toCalendarEvent(item) {
      const date = salonDateOnly(item.appointmentDate);
      const status = statusStyles[item.status] || { label: item.status, color: '#64748b' };
      const customer = item.contactName || item.contactPhone || '未填姓名';
      return {
        id: item.id,
        title: `${customer}・${item.stylistName}`,
        start: `${date}T${this.shortTime(item.startTime)}`,
        end: `${date}T${this.shortTime(item.endTime)}`,
        backgroundColor: status.color,
        borderColor: status.color,
        classNames: item.status === 'cancelled' ? ['is-cancelled'] : [],
        extendedProps: { reservation: item, statusLabel: status.label }
      };
    },
    openReservation(info) {
      if (info.event.extendedProps.closure) return;
      this.$router.push({
        name: 'SalonReservationEdit',
        params: { id: info.event.id }
      });
    },
    async loadCalendarClosures() {
      const params = {
        page: 1,
        pageSize: 100,
        storeId: this.selectedStoreId,
        status: 'active'
      };
      const firstResponse = await salonService.listClosures(params);
      const firstData = firstResponse?.data || {};
      const items = [...(firstData.items || [])];
      const totalPages = firstData.pagination?.totalPages || 1;
      if (totalPages > 1) {
        const remaining = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) =>
            salonService.listClosures({ ...params, page: index + 2 })
          )
        );
        remaining.forEach(response => items.push(...(response?.data?.items || [])));
      }
      return items.filter(item => item.isAllDay);
    },
    toClosureBackgroundEvents(closures, rangeStart, rangeEnd) {
      const events = [];
      const startDate = localDateKey(rangeStart);
      const endDate = localDateKey(rangeEnd);
      for (const closure of closures) {
        for (let date = startDate; date < endDate; date = addDateDays(date, 1)) {
          if (!closureAppliesOnDate(closure, date)) continue;
          events.push({
            id: `closure-${closure.id}-${date}`,
            title: closure.name,
            start: date,
            end: addDateDays(date, 1),
            allDay: true,
            display: 'background',
            backgroundColor: '#fecaca',
            classNames: ['salon-closure-background'],
            extendedProps: { closure }
          });
        }
      }
      return events;
    },
    openReservationItem(item) {
      this.$router.push({
        name: 'SalonReservationEdit',
        params: { id: item.id }
      });
    },
    reservationTimestamp(item) {
      const date = salonDateOnly(item.appointmentDate);
      return new Date(`${date}T${this.shortTime(item.startTime)}:00+08:00`).getTime();
    },
    displayReservationDate(item) {
      const date = salonDateOnly(item.appointmentDate);
      const label = new Intl.DateTimeFormat('zh-TW', {
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
        timeZone: 'Asia/Taipei'
      }).format(new Date(`${date}T00:00:00+08:00`));
      return `${label} ${this.shortTime(item.startTime)}`;
    },
    statusStyle(status) {
      return statusStyles[status] || { label: status, color: '#64748b' };
    },
    shortTime(value) {
      return String(value || '').slice(0, 5);
    },
    async handleError(error, fallback) {
      const message = error?.response?.data?.message || error?.message || fallback;
      await showError('操作失敗', message);
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.store-filter,
.calendar-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 20px;
  padding: 20px;
}

.section-heading {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.section-heading h2 {
  color: #1e293b;
  font-size: 18px;
  margin: 0;
}

.section-heading p {
  color: #64748b;
  font-size: 13px;
  margin: 4px 0 0;
}

.reservation-count {
  background: #eef2ff;
  border-radius: 999px;
  color: #4338ca;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 12px;
  white-space: nowrap;
}

.calendar-wrapper {
  min-height: 560px;
  position: relative;
}

.display-mode-toolbar {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.display-mode-switch {
  background: #f1f5f9;
  border-radius: 9px;
  display: inline-flex;
  padding: 4px;
}

.display-mode-switch button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  font-weight: 600;
  gap: 6px;
  padding: 8px 14px;
}

.display-mode-switch button.active {
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.14);
  color: #4338ca;
}

.upcoming-toggle {
  align-items: center;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  gap: 7px;
}

.reservation-list-view { position: relative; }
.list-loading {
  align-items: center;
  color: #475569;
  display: flex;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  min-height: 260px;
}
.reservation-table { min-width: 920px; }
.reservation-table td,
.reservation-table th { vertical-align: middle; }
.status-badge {
  background: color-mix(in srgb, var(--status-color) 12%, white);
  border: 1px solid color-mix(in srgb, var(--status-color) 35%, white);
  border-radius: 999px;
  color: var(--status-color);
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 9px;
  white-space: nowrap;
}

.hours-warning {
  align-items: center;
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 10px;
  color: #9a3412;
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
  padding: 12px 14px;
}

.hours-warning > i {
  color: #ea580c;
  flex-shrink: 0;
  font-size: 24px;
}

.hours-warning > div {
  display: grid;
  flex: 1;
  gap: 2px;
}

.hours-warning span {
  font-size: 13px;
}

.calendar-legend {
  align-items: center;
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 16px;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.calendar-legend span {
  align-items: center;
  display: inline-flex;
  gap: 6px;
}

.legend-swatch {
  border: 1px solid #cbd5e1;
  border-radius: 3px;
  display: inline-block;
  height: 12px;
  width: 18px;
}

.legend-swatch.business {
  background: #fff;
}

.legend-swatch.non-business {
  background: repeating-linear-gradient(
    -45deg,
    #e2e8f0,
    #e2e8f0 3px,
    #f8fafc 3px,
    #f8fafc 6px
  );
}

.legend-swatch.closure {
  background: #fecaca;
  border-color: #f87171;
}

.calendar-loading {
  align-items: center;
  background: rgba(255, 255, 255, 0.82);
  color: #475569;
  display: flex;
  font-weight: 600;
  gap: 8px;
  inset: 0;
  justify-content: center;
  position: absolute;
  z-index: 5;
}

.empty-state {
  align-items: center;
  color: #64748b;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  min-height: 280px;
  text-align: center;
}

.empty-state i {
  color: #94a3b8;
  font-size: 42px;
}

.empty-state strong {
  color: #334155;
  font-size: 17px;
}
.empty-state.compact { min-height: 260px; }

.spin {
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

:deep(.fc .fc-button-primary) {
  background-color: #fff;
  border-color: #cbd5e1;
  color: #334155;
}

:deep(.fc .fc-button-primary:hover),
:deep(.fc .fc-button-primary:not(:disabled).fc-button-active) {
  background-color: #4f46e5;
  border-color: #4f46e5;
  color: #fff;
}

:deep(.fc .fc-toolbar-title) {
  color: #1e293b;
  font-size: 20px;
}

:deep(.fc .fc-col-header-cell.fc-day-sat) {
  background-color: #dcfce7;
}

:deep(.fc .fc-col-header-cell.fc-day-sun) {
  background-color: #fee2e2;
}

:deep(.fc .fc-col-header-cell.fc-day-sat .fc-col-header-cell-cushion),
:deep(.fc .fc-col-header-cell.fc-day-sun .fc-col-header-cell-cushion) {
  color: #111827;
}

:deep(.fc .fc-event) {
  cursor: pointer;
  padding: 2px 4px;
}

:deep(.fc .fc-event.is-cancelled) {
  opacity: 0.62;
  text-decoration: line-through;
}

:deep(.fc .fc-non-business) {
  background: repeating-linear-gradient(
    -45deg,
    rgba(203, 213, 225, 0.58),
    rgba(203, 213, 225, 0.58) 4px,
    rgba(241, 245, 249, 0.72) 4px,
    rgba(241, 245, 249, 0.72) 8px
  );
  opacity: 1;
}

:deep(.fc .salon-closure-background) {
  background-color: #fecaca !important;
  opacity: 0.78;
}

@media (max-width: 767px) {
  .page-header,
  .section-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .reservation-count {
    align-self: flex-start;
  }

  .calendar-card,
  .store-filter {
    padding: 14px;
  }

  .display-mode-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .display-mode-switch {
    align-self: flex-start;
  }

  .hours-warning {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .hours-warning .btn {
    margin-left: 36px;
  }

  :deep(.fc .fc-header-toolbar) {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  :deep(.fc .fc-toolbar-chunk) {
    display: flex;
    justify-content: center;
  }

  :deep(.fc .fc-toolbar-title) {
    font-size: 17px;
  }

  :deep(.fc .fc-timegrid-axis),
  :deep(.fc .fc-timegrid-slot-label) {
    font-size: 11px;
  }
}
</style>
